/**
 * Database operations for Creature Superpowers Survey Responses
 * Privacy-conscious: No personal identifying information collected
 */

import { supabase, getSessionId } from './supabase';
import type { CreatureType } from '../components/creatureSuperpowers/types';

export interface SurveyResponse {
  sessionId: string;
  creatureType: CreatureType;
  creatureName?: string;
  selectedPowers?: string[]; // Array of power IDs
  feelingEmoji: string;
  feelingLabel: string;
  favoritePartEmoji: string;
  favoritePartLabel: string;
  language: 'en' | 'es';
  gameDurationSeconds?: number;
  userAgent?: string;
  screenSize?: string;
}

/**
 * Save survey response to Supabase
 * @param response - Survey response data
 * @returns Success status and any error
 */
export async function saveSurveyResponse(response: SurveyResponse) {
  try {
    const { data, error } = await supabase
      .from('creature_survey_responses')
      .insert({
        session_id: response.sessionId,
        creature_type: response.creatureType,
        creature_name: response.creatureName || null,
        selected_powers: response.selectedPowers ? JSON.stringify(response.selectedPowers) : null,
        feeling_emoji: response.feelingEmoji,
        feeling_label: response.feelingLabel,
        favorite_part_emoji: response.favoritePartEmoji,
        favorite_part_label: response.favoritePartLabel,
        language: response.language,
        game_duration_seconds: response.gameDurationSeconds || null,
        user_agent: response.userAgent || null,
        screen_size: response.screenSize || null
      });

    if (error) {
      console.error('Error saving survey response:', error);
      return { success: false, error };
    }

    console.log('Survey response saved successfully:', data);
    return { success: true, data };
  } catch (error) {
    console.error('Unexpected error saving survey response:', error);
    return { success: false, error };
  }
}

/**
 * Get device screen size category for analytics
 */
export function getScreenSize(): string {
  if (typeof window === 'undefined') return 'unknown';

  const width = window.innerWidth;
  if (width < 640) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Get survey response data ready for submission
 */
export function prepareSurveyData(
  feelingEmoji: string,
  feelingLabel: string,
  favoritePartEmoji: string,
  favoritePartLabel: string,
  language: 'en' | 'es',
  creatureType?: CreatureType,
  creatureName?: string,
  selectedPowers?: string[],
  gameDurationSeconds?: number
): SurveyResponse {
  return {
    sessionId: getSessionId(),
    creatureType: creatureType || 'dragon', // Default if not provided
    creatureName,
    selectedPowers,
    feelingEmoji,
    feelingLabel,
    favoritePartEmoji,
    favoritePartLabel,
    language,
    gameDurationSeconds,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    screenSize: getScreenSize()
  };
}
